import { useState, useEffect } from 'react';
import HttpInterceptor from '../lib/HttpInterceptor';

export function useServiceCounts() {
    const [serviceCounts, setServiceCounts] = useState({});

    useEffect(() => {
        let isMounted = true;

        const fetchCounts = async () => {
            try {
                const [trainersResponse, settingsResponse] = await Promise.all([
                    HttpInterceptor.get('/api/trainers', {
                        params: { limit: 1000 },
                        showErrorToast: false
                    }),
                    HttpInterceptor.get('/api/admin/public/settings', { showErrorToast: false }).catch(() => null)
                ]);

                if (trainersResponse.status === 200 && isMounted) {
                    let trainers = [];
                    if (Array.isArray(trainersResponse.data)) {
                        trainers = trainersResponse.data;
                    } else if (trainersResponse.data?.data && Array.isArray(trainersResponse.data.data)) {
                        trainers = trainersResponse.data.data;
                    } else if (trainersResponse.data?.trainers && Array.isArray(trainersResponse.data.trainers)) {
                        trainers = trainersResponse.data.trainers;
                    }

                    const counts = {};

                    trainers.forEach((trainer) => {
                        let services = [];
                        if (trainer.services && Array.isArray(trainer.services) && trainer.services.length > 0) {
                            services = trainer.services;
                        } else if (trainer.service !== undefined && trainer.service !== null) {
                            services = [trainer.service];
                        }

                        // Create a unique list of services for this trainer to avoid double counting
                        // if a trainer accidentally has duplicates in their services array
                        const uniqueServices = [...new Set(services.map(s =>
                            s !== null && s !== undefined ? String(s).trim() : ''
                        ).filter(Boolean))];

                        uniqueServices.forEach(serviceName => {
                            // Normalize the service name similarly to Services.jsx
                            let cleanName = serviceName.replace(/\s*Programs?$/i, '').trim();

                            // Map API names to standardized types
                            const typeMap = {
                                'Yoga Trainer': 'yoga-trainer',
                                'Yoga': 'yoga-trainer',
                                'Dietitian': 'dietitian',
                                'Gym Trainer': 'gym-trainer',
                                'Gym Instructor': 'gym-trainer',
                                'Gym': 'gym-trainer',
                                'Zumba Trainer': 'zumba-trainer',
                                'Zumba Instructor': 'zumba-trainer',
                                'Zumba': 'zumba-trainer',
                                'Reiki Trainer': 'reiki-trainer',
                                'Reiki Instructor': 'reiki-trainer',
                                'Reiki Healer': 'reiki-healer',
                                'Reiki Consultant': 'reiki-healer',
                                'Reiki': 'reiki-healer',
                                'Astrologer': 'astrologer',
                                'Astrology': 'astrologer',
                                'Numerologer': 'numerologer',
                                'Numerology': 'numerologer',
                                'Ayurveda Practitioner': 'ayurvedacharya',
                                'Ayurvedacharya': 'ayurvedacharya',
                                'Ayurveda': 'ayurvedacharya',
                                'Home Tuition': 'home-tuition',
                                'Tarot Card Trainer': 'tarot-card-trainer',
                                'Tarot Instructor': 'tarot-card-trainer',
                                'Tarot Card Reader': 'tarot-card-reader',
                                'Tarot': 'tarot-card-reader'
                            };

                            let mappedType = null;

                            // Exact match first
                            if (typeMap[cleanName]) {
                                mappedType = typeMap[cleanName];
                            } else {
                                // Case-insensitive match
                                const lowerCleanName = cleanName.toLowerCase();
                                for (const [key, value] of Object.entries(typeMap)) {
                                    if (key.toLowerCase() === lowerCleanName) {
                                        mappedType = value;
                                        break;
                                    }
                                }
                            }

                            // Fallback to parameterized format
                            if (!mappedType) {
                                mappedType = cleanName.toLowerCase().replace(/\s+/g, '-');
                            }

                            // Increment count
                            counts[mappedType] = (counts[mappedType] || 0) + 1;
                        });
                    });

                    // Add AI consultants if enabled
                    const isAiAgentsEnabled = settingsResponse?.data?.data?.isAiAgentsEnabled;
                    if (isAiAgentsEnabled) {
                        const aiTypes = ['dietitian', 'astrologer', 'ayurvedacharya', 'reiki-healer', 'numerologer', 'tarot-card-reader', 'home-tutor'];
                        aiTypes.forEach(type => {
                            counts[type] = (counts[type] || 0) + 1;
                        });
                    }

                    setServiceCounts(counts);
                }
            } catch (err) {
                console.error('Error fetching service counts for header menus:', err);
            }
        };

        fetchCounts();

        return () => {
            isMounted = false;
        };
    }, []);

    return serviceCounts;
}
